import React from "react"

import SignaturePad from "react-signature-canvas"

import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { useApplication } from "@/contexts/ApplicationContext"
import { getVRFContractText } from "@/utils/contractUtils"
import { replaceContractPlaceholders } from "@/utils/pdfUtils"

interface VehicleReleaseFormProps {
  onSign: (signature: string | null) => void
  signature: string | null
  disabled?: boolean
}

const VehicleReleaseForm: React.FC<VehicleReleaseFormProps> = ({
  onSign,
  signature,
  disabled = false,
}) => {
  const signaturePad = React.useRef<SignaturePad | null>(null)
  const { application } = useApplication()

  const clear = () => {
    if (signaturePad.current) {
      signaturePad.current.clear()
      onSign(null)
    }
  }

  const save = () => {
    if (signaturePad.current && !signaturePad.current.isEmpty()) {
      const signatureData = signaturePad.current.toDataURL()
      onSign(signatureData)
    }
  }
  // Prepare processed contract with placeholders replaced (same as PDF generation)
  const applicantName =
    `${application?.basicDetails?.firstName || ""} ${
      application?.basicDetails?.lastName || ""
    }`.trim() || "Applicant"

  const vrfContract = replaceContractPlaceholders(getVRFContractText(), {
    "full-name": applicantName,
  })
  return (
    <div className="space-y-6">
      <div className="rounded-lg border bg-white shadow-sm">
        <div className="border-b bg-gray-50 p-6">
          <h3 className="text-xl font-semibold text-jungle">Vehicle Release Agreement</h3>
          <p className="mt-1 text-sm text-gray-600">
            Please read the agreement carefully before signing
          </p>
        </div>

        <div className="h-96 space-y-4 overflow-y-auto p-6 text-base leading-relaxed">
          {vrfContract ? (
            <div
              className="contract-content prose w-full max-w-full whitespace-pre-wrap"
              dangerouslySetInnerHTML={{ __html: vrfContract }}
            />
          ) : (
            <div className="flex h-64 items-center justify-center">
              <p className="text-gray-500">Contract not available</p>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <Label className="text-lg font-medium">Your Signature</Label>
        {signature ? (
          <div className="space-y-3">
            <div className="rounded-md border bg-white p-4 shadow-sm">
              <img src={signature} alt="Signature" className="max-h-40 w-full object-contain" />
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onSign(null)}
              className="w-full md:w-auto"
              disabled={disabled}
            >
              Clear Signature
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="touch-none rounded-md border bg-white shadow-sm">
              <SignaturePad
                ref={signaturePad}
                canvasProps={{
                  className: "w-full h-40 touch-none",
                }}
              />
            </div>
            <div className="flex flex-col gap-2 md:flex-row">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={clear}
                className="w-full md:w-auto"
                disabled={disabled}
              >
                Clear
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={save}
                className="w-full bg-honeycomb hover:bg-amber-500 md:w-auto"
                disabled={disabled}
              >
                Sign Agreement
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default VehicleReleaseForm
